import { BoxProps, IconProps, useDisclosure } from '@chakra-ui/react';
import { isEmpty } from 'lodash';
import { useSelector } from 'react-redux';
import { Creator } from '~/clientApi/creator.model';
import { SOCIAL_PLATFORMS } from '~/constants';
import {
  AGE_TOOLTIP_MESSAGE,
  COUNTRY_TOOLTIP_MESSAGE,
  GENDER_TOOLTIP_MESSAGE,
} from '~/screens/MediaPackDetailDefaultScreen/MediaPackDetailDefaultScreen.constants';
import { AgesPieChart } from '~/screens/MediaPackDetailDefaultScreen/components/AgeDistributionChart/components/AgesPieChart';
import { GenderDistribution } from '~/screens/MediaPackDetailDefaultScreen/components/GenderDistribution';
import { TopCountries } from '~/screens/MediaPackDetailDefaultScreen/components/TopCountries';
import { selectCreator } from '~/selectors/creator';
import {
  Country,
  CreatorBenchmarks,
  DefaultMediaPackType,
  OptionType,
} from '~/types';
import { getCreatorPlatformConnectionStatus } from '~/utils';

import { LastUpdatedPlatform } from '../../LastUpdatedPlatform';
import { MediaPackDetailEditHighlightsModal } from '../../MediaPackDetailEditHighlightsModal';
import { MetricCardData } from './OveriewSection.types';
import { OverviewLayout } from './components/Layout';
import { MetricCard } from './components/MetricCard';
import { SingleMetricCard } from './components/SingleMetricCard';

interface Props extends BoxProps {
  overviewTitle: string;
  metrics: MetricCardData[];
  ages: OptionType<number>[];
  genders: OptionType<number>[];
  countries: Country<number>[];
  icon: React.FC<IconProps>;
  platform: SOCIAL_PLATFORMS;
  creatorBenchmarks: CreatorBenchmarks;
  mediaPack: DefaultMediaPackType;
  isSharedView?: boolean;
  insightsLastUpdatedAt?: string;
}

export const OverviewSection: React.FC<Props> = ({
  overviewTitle = '',
  metrics,
  ages = [],
  genders = [],
  countries = [],
  creatorBenchmarks,
  mediaPack,
  platform,
  isSharedView,
  insightsLastUpdatedAt,
  ...props
}) => {
  const { isOpen, onClose, onOpen } = useDisclosure();
  const creator = useSelector(selectCreator);
  const platformConnection = getCreatorPlatformConnectionStatus(
    creator as Creator,
  )[platform];

  return (
    <>
      <MediaPackDetailEditHighlightsModal
        benchmarks={creatorBenchmarks}
        mediaPack={mediaPack}
        isOpen={isOpen}
        onClose={onClose}
        platform={platform}
      />

      <OverviewLayout
        onEdit={onOpen}
        title={overviewTitle}
        platform={platform}
        isSharedView={isSharedView}
        {...props}
      >
        {metrics.map(
          (metric) =>
            !isEmpty(metric.value) && (
              <SingleMetricCard
                key={`metric-${metric.key}`}
                metric={metric}
                lastUpdatedComponent={
                  <LastUpdatedPlatform
                    platformConnection={platformConnection}
                    creatorBenchmarks={creatorBenchmarks}
                    platform={platform}
                  />
                }
              />
            ),
        )}

        <MetricCard label="Age Distribution" tooltipInfo={AGE_TOOLTIP_MESSAGE}>
          <AgesPieChart ages={ages} />
          <LastUpdatedPlatform
            platformConnection={platformConnection}
            insightsLastUpdatedAt={insightsLastUpdatedAt}
          />
        </MetricCard>

        <MetricCard
          label="Gender Distribution"
          tooltipInfo={GENDER_TOOLTIP_MESSAGE}
        >
          <GenderDistribution
            marginTop={6}
            overallGenderDistribution={genders}
            width="50%"
            stackProps={{
              spacing: 6,
            }}
          />
          <LastUpdatedPlatform
            platformConnection={platformConnection}
            insightsLastUpdatedAt={insightsLastUpdatedAt}
          />
        </MetricCard>

        <MetricCard
          label="Top Geographies"
          tooltipInfo={COUNTRY_TOOLTIP_MESSAGE}
        >
          <TopCountries
            iconsContainerProps={{
              gap: 12,
            }}
            marginTop={6}
            overallTopCountries={countries}
            width="50%"
          />
          <LastUpdatedPlatform
            platformConnection={platformConnection}
            insightsLastUpdatedAt={insightsLastUpdatedAt}
          />
        </MetricCard>
      </OverviewLayout>
    </>
  );
};
